import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import React from "react";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import organizationservice from "../../../service/organizationservice";
import errorHandler from "../../../utils/errorHandler";
import { buildDateParams, computePresetRange, DATE_PRESET_OPTIONS, DATE_PRESETS, normalizeDatePreset } from "./DateRangePreset";

const FILE_HEADERS = [
  { label: "Business Name", value: "Business Name" },
  { label: "Last Transmission Date", value: "Last Transmission Date" },
  { label: "Joined Date", value: "Joined Date" },
  { label: "Uploads", value: "Uploads" },
  { label: "Transmissions", value: "Transmissions" },
  { label: "Successful", value: "Successful" },
  { label: "Processing", value: "Processing" },
  { label: "Failed", value: "Failed" },
];

const API_HEADERS = [
  { label: "Business Name", value: "Business Name" },
  { label: "Last Transmission Date", value: "Last Transmission Date" },
  { label: "Total API Calls", value: "Total API Calls" },
  { label: "Total Signed", value: "Invoices Signed" },
  { label: "Signed Successful", value: "Signed Successful" },
  { label: "Signed Failed", value: "Signed Failed" },
  { label: "Transmission Initiation", value: "Transmission Initiation" },
  { label: "Payment Reported", value: "Payment Reported" },
];

const EXPORT_PAGE_SIZE = 100;
const MAX_EXPORT_PAGES = 200;

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const presetLabel = (preset) => DATE_PRESET_OPTIONS.find((option) => option.value === preset)?.label ?? "This month";

/**
 * Human-readable bounds for the active preset, used for the summary line and file name.
 * CUSTOM uses the table's own dates; every other preset is resolved locally the same
 * way the backend resolves it.
 */
function resolveRangeBounds(preset, dateFrom, dateTo) {
  if (preset === DATE_PRESETS.ALL_TIME) return { from: null, to: null };

  if (preset === DATE_PRESETS.CUSTOM) {
    if (!dateFrom || !dateTo) return { from: null, to: null };
    return { from: moment(dateFrom), to: moment(dateTo) };
  }

  const range = computePresetRange(preset);
  if (!range?.dateFrom || !range?.dateTo) return { from: null, to: null };
  return {
    from: moment(range.dateFrom),
    to: moment(range.dateTo).subtract(1, "day"),
  };
}

const ExportTransmissionModal = ({ isOpen, onClose, mode, filters = {} }) => {
  const isApiMode = mode === "api";
  const headers = isApiMode ? API_HEADERS : FILE_HEADERS;

  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedHeaders, setSelectedHeaders] = React.useState([]);

  const datePreset = normalizeDatePreset(filters.datePreset);
  const bounds = resolveRangeBounds(datePreset, filters.dateFrom, filters.dateTo);

  const activeQuery = {
    keyword: filters.keyword || undefined,
    status: filters.status || undefined,
    sortBy: filters.sortBy || undefined,
    sortOrder: filters.sortOrder || undefined,
    ...buildDateParams(datePreset, filters.dateFrom, filters.dateTo),
  };

  const fetchAllPages = async () => {
    const rows = [];
    let page = 1;

    while (page <= MAX_EXPORT_PAGES) {
      const { data } = await organizationservice.getFirsConnected({
        ...activeQuery,
        page,
        limit: EXPORT_PAGE_SIZE,
        mode,
      });
      const pageRows = Array.isArray(data?.data) ? data.data : [];
      rows.push(...pageRows);

      if (!pageRows.length || !data?.hasNextPage) break;
      page += 1;
    }

    return rows;
  };

  const getTransmittedInvoices = async () => {
    if (!selectedHeaders.length) {
      errorHandler("Select at least one header to export.");
      return;
    }

    try {
      setIsLoading(true);
      const transmissions = await fetchAllPages();

      if (!transmissions.length) {
        errorHandler("No records found for the current filters.");
        return;
      }

      const safeFileName = formatFileName(isApiMode ? "ApiConnectedOrganizations" : "ConnectedOrganizations", datePreset, bounds);
      downloadCSV(transmissions, selectedHeaders, safeFileName);
    } catch (error) {
      errorHandler(error?.response);
    } finally {
      setIsLoading(false);
    }
  };

  const allSelected = selectedHeaders.length === headers.length;

  const handleSelectChange = (_, newValue) => {
    const isSelectAllClicked = newValue.some((v) => v.value === "Select All");

    if (isSelectAllClicked) {
      setSelectedHeaders(allSelected ? [] : headers);
    } else {
      setSelectedHeaders(newValue);
    }
  };

  const options = [{ label: "Select All", value: "Select All" }, ...headers];

  const rangeText = bounds.from && bounds.to ? `${bounds.from.format("Do MMM, YYYY")} - ${bounds.to.format("Do MMM, YYYY")}` : "All time";

  const summaryRows = [
    { label: "Range", value: `${presetLabel(datePreset)} (${rangeText})` },
    filters.keyword ? { label: "Search", value: `"${filters.keyword}"` } : null,
    filters.status ? { label: "Status", value: filters.status } : null,
    filters.sortBy
      ? {
          label: "Sort",
          value: `${filters.sortBy} (${filters.sortOrder || "desc"})`,
        }
      : null,
  ].filter(Boolean);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Export</DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 5 }}>
        <Box mb={4} p={3} sx={{ bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Exporting what the table currently shows
          </Typography>
          {summaryRows.map((row) => (
            <Box key={row.label} display="flex" gap={2} mb={1}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 64 }}>
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: "break-word" }}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box mb={4}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={options}
            getOptionLabel={(option) => option.label}
            value={selectedHeaders}
            onChange={handleSelectChange}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              const isSelectAll = option.value === "Select All";

              const checked = isSelectAll && allSelected ? true : isSelectAll ? false : selected;

              return (
                <li key={key} {...optionProps}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={checked} />
                  {option.label}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" variant="outlined" label="Select Headers" placeholder="Select Headers" />
            )}
          />
        </Box>

        <Box mb={4}>
          <Select fullWidth disabled value="csv" onChange={(e) => {}} variant="outlined" size="small">
            <MenuItem value="csv">CSV</MenuItem>
          </Select>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ gap: 2 }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Close
        </Button>
        <Button disabled={isLoading} variant="contained" color="secondary" onClick={getTransmittedInvoices}>
          {isLoading ? "Please wait..." : "Export"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function formatFileName(base, datePreset, bounds) {
  const range =
    bounds.from && bounds.to
      ? `${bounds.from.format("YYYY-MM-DD")}_to_${bounds.to.format("YYYY-MM-DD")}`
      : String(datePreset).toLowerCase();
  const timestamp = moment().format("YYYY-MM-DD");
  return `${base}_${range}_${timestamp}.csv`;
}

export function downloadCSV(data, selectedHeaders, fileName = "invoices.csv") {
  if (!data || data.length === 0) {
    console.warn("No data available for CSV export.");
    return;
  }

  if (!selectedHeaders || selectedHeaders.length === 0) {
    console.warn("No headers selected for CSV export.");
    return;
  }

  const headerLabels = selectedHeaders.map((h) => h.label);
  const csvRows = [headerLabels.join(",")];

  const getValue = (item, key) => {
    switch (key) {
      case "Business Name":
        return item?.name || "";

      case "Last Transmission Date": {
        const date = item?.lastTransmissionAt ?? item?.lastTransmissionDate ?? item?.lastSyncedAt;
        return date ? moment(date).format("Do MMM, YYYY, h:mm A") : "N/A";
      }

      case "Joined Date": {
        const date = item?.joinedAt ?? item?.joinDate;
        return date ? moment(date).format("Do MMM, YYYY") : "N/A";
      }

      case "Uploads":
        return item?.totalUploads ?? item?.totalFileUpload ?? item?.transmission?.totalFileUpload ?? 0;

      case "Transmissions":
        return item?.totalTransmissions ?? item?.transmission?.total ?? 0;

      case "Successful":
        return item?.totalSuccessful ?? item?.successful ?? item?.transmission?.successful ?? 0;

      case "Processing":
        return item?.totalPending ?? item?.pending ?? item?.transmission?.pending ?? 0;

      case "Failed":
        return item?.totalFailed ?? item?.failed ?? item?.transmission?.failed ?? 0;

      case "Total API Calls":
        return item?.totalApiCalls ?? 0;

      case "Invoices Signed":
        return item?.totalInvoicesSigned ?? 0;

      case "Signed Successful":
        return item?.totalInvoicesSignedSuccessful ?? 0;

      case "Signed Failed":
        return item?.totalInvoicesSignedFailed ?? 0;

      case "Transmission Initiation":
        return item?.totalTransmissionInitiation ?? 0;

      case "Payment Reported":
        return item?.totalPaymentReported ?? 0;

      default:
        return "";
    }
  };

  for (const invoice of data) {
    const row = selectedHeaders
      .map((h) => {
        const raw = getValue(invoice, h.value);
        const safe = String(raw).replace(/"/g, '""');
        return `"${safe}"`;
      })
      .join(",");
    csvRows.push(row);
  }

  const csvContent = csvRows.join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default ExportTransmissionModal;
